
import { defineComponent, ref } from "vue";
import { useStore } from "vuex";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import { useForm } from "vee-validate";
import { Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import { WarningFilled } from "@element-plus/icons-vue";

import ApiService from "@/core/services/ApiService";
import { DrawerComponent } from "@/assets/ts/components/_DrawerComponent";

interface MerchantDetails {
  corp_name: string;
  merchant_type: string;
  mid_number: string;
  mid_descriptor: string;
  dba_name: string;
  crm_uuid: string;
  gw_uuid: string;
  ethoca_member_id: string;
  verifi_merchant_id: string;
  mcc: string;
  logo_url: string;
}

interface MerchantProduct {
  product_name: string;
  product_description: string;
  product_url: string;
  product_price: string;
  fulfill_type: string;
}

interface CheckoutPage {
  url: string;
  url_phone: string;
  email: string;
  terms_url: string;
  cancel_url: string;
  contact_url: string;
  refund_url: string;
  logo_url: string;
}

export default defineComponent({
  name: "update_queue_drawer",
  emit: ["submit"],
  components: {
    Field,
    ErrorMessage,
    WarningFilled,
  },
  props: {
    merchant: Object,
  },
  watch: {
    merchant(val) {
      this.activeNames.splice(0);
      this.initImportFields(val);
      this.componentKey += 1;
    },
  },
  data() {
    return {
      componentKey: 0,
    };
  },
  setup(props, { emit }) {
    const store = useStore();
    const submitButton = ref<HTMLButtonElement | null>(null);
    const merchantDetails = ref<MerchantDetails>({
      corp_name: "",
      merchant_type: "",
      mid_number: "",
      mid_descriptor: "",
      dba_name: "",
      crm_uuid: "",
      gw_uuid: "",
      ethoca_member_id: "",
      verifi_merchant_id: "",
      mcc: "",
      logo_url: "",
    });
    const merchantProduct = ref<MerchantProduct>({
      product_name: "",
      product_description: "",
      product_url: "",
      product_price: "",
      fulfill_type: "",
    });
    const checkoutPage = ref<CheckoutPage>({
      url: "",
      url_phone: "",
      email: "",
      terms_url: "",
      cancel_url: "",
      contact_url: "",
      refund_url: "",
      logo_url: "",
    });
    const activeNames = ref([]);

    const initImportFields = (merchant) => {
      if (merchant) {
        Object.keys(merchantDetails.value).forEach((key) => {
          if (merchant[key]) {
            merchantDetails.value[key] = merchant[key];
          }
        });
        if (merchant.merchantDetails) {
          const data = JSON.parse(merchant.merchantDetails);
          Object.keys(merchantDetails.value).forEach((key) => {
            if (data[key]) {
              merchantDetails.value[key] = data[key];
            }
          });
        }
        if (merchant.merchantProduct) {
          const data = JSON.parse(merchant.merchantProduct);
          Object.keys(merchantProduct.value).forEach((key) => {
            if (data[key]) {
              merchantProduct.value[key] = data[key];
            }
          });
        }
        if (merchant.checkoutPage) {
          const data = JSON.parse(merchant.checkoutPage);
          Object.keys(checkoutPage.value).forEach((key) => {
            if (data[key]) {
              checkoutPage.value[key] = data[key];
            }
          });
        }
      }
    };

    const formSubmit = async () => {
      // eslint-disable-next-line
      submitButton.value!.disabled = true;
      // Activate indicator
      submitButton.value?.setAttribute("data-kt-indicator", "on");

      // eslint-disable-next-line
      const payload: any = {
        customer_id: store.getters.currentUser.data.customer_id,
        group_id: store.getters.currentUser.data.companygroupid,
        ...merchantDetails.value,
        ...merchantProduct.value,
        ...checkoutPage.value,
      };

      try {
        const res = await ApiService.post(
          "merchants/queue?action=insert_merchant",
          payload
        );
        if (res.data.success) {
          const res4Delete = await ApiService.delete(
            `merchants/queue?merchant_id=${props.merchant?.merchant_id}`
          );
          if (res4Delete.data.success) {
            Swal.fire({
              text: "You have successfully Saved the merchant!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-primary",
              },
            }).then(async function () {
              DrawerComponent?.getInstance("update_queue_drawer")?.hide();
              emit("submit");
            });
          } else {
            Swal.fire({
              text: res.data.error.message,
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Try again!",
              customClass: {
                confirmButton: "btn fw-bold btn-light-danger",
              },
            });
          }
        } else {
          Swal.fire({
            text: res.data.error.message,
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: "Try again!",
            customClass: {
              confirmButton: "btn fw-bold btn-light-danger",
            },
          });
        }
      } catch (e) {
        Swal.fire({
          text: e.response.data.error
            ? e.response.data.error.message
            : "Something went wrong!",
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: "Try again!",
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }

      submitButton.value?.removeAttribute("data-kt-indicator");
      // eslint-disable-next-line
      submitButton.value!.disabled = false;
    };

    const merchantSchema = Yup.object({
      merchant_type: Yup.string().required().label("Merchant type"),
      mid_number: Yup.string().required().label("Mid number"),
      processor_id: Yup.string().required().label("Processor"),
      corp_name: Yup.string().required().label("Corp name"),
      dba_name: Yup.string().required().label("DBA name"),
      mid_descriptor: Yup.string().required().label("Mid descriptor"),
      crm_uuid: Yup.string().required().label("CRM UUID"),
      gw_uuid: Yup.string().required().label("GW UUID"),
      mcc: Yup.string().required().label("MCC"),
      logo_url: Yup.string().required().label("Logo url"),
      product_name: Yup.string().required().label("Product name"),
      product_description: Yup.string().required().label("Product description"),
      product_url: Yup.string().required().label("Product Url"),
      product_price: Yup.string().required().label("Product price"),
      fulfill_type: Yup.string().required().label("Fulfill type"),
      url: Yup.string().required().label("Checkout Url"),
      url_phone: Yup.string().required().label("Phone number"),
      email: Yup.string().email().required().label("Email"),
    });
    const { errors } = useForm<
      MerchantDetails | MerchantProduct | CheckoutPage
    >({
      validationSchema: merchantSchema,
    });

    return {
      merchantDetails,
      merchantProduct,
      activeNames,
      checkoutPage,
      submitButton,
      formSubmit,
      errors,
      initImportFields,
    };
  },
});
